import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

/**
 * Legal pages — Terms of Service + Privacy Policy.
 *
 * ⚠️ STARTER TEMPLATE — not legal advice. Owner should have a UK solicitor
 * review before final publish, especially the data-processing + UK GDPR
 * + Stripe billing clauses. Updated dates auto-render from LAST_UPDATED.
 *
 * One page handles both routes; the active tab is derived from the
 * pathname so deep-links work (e.g. share to /privacy directly).
 */
const LAST_UPDATED = "9 June 2026";
const COMPANY = "Fairway Golf";
const SITE = "fairway-golf.org";
const CONTACT_EMAIL = "hello@fairway-golf.org";

export default function Legal() {
  const { pathname } = useLocation();
  const isPrivacy = pathname.startsWith("/privacy");
  const active = isPrivacy ? "privacy" : "terms";

  return (
    <div className="min-h-screen bg-[var(--bg-app)]" data-testid="legal-page">
      {/* Top bar */}
      <div className="border-b border-[var(--border)] bg-white">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center gap-3">
          <Link to="/" className="btn-ghost -ml-2" data-testid="legal-back-link">
            <ArrowLeft size={20} weight="bold" />
          </Link>
          <img src="/fairway-logo.png" alt="Fairway" className="h-11 w-auto rounded-lg" />
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-5 pt-6">
        <div className="inline-flex rounded-full p-1 bg-white border border-[var(--border)]">
          <Link
            to="/terms"
            data-testid="legal-tab-terms"
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
              active === "terms"
                ? "bg-[var(--primary)] text-white"
                : "text-[var(--text-secondary)]"
            }`}
          >
            Terms of service
          </Link>
          <Link
            to="/privacy"
            data-testid="legal-tab-privacy"
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
              active === "privacy"
                ? "bg-[var(--primary)] text-white"
                : "text-[var(--text-secondary)]"
            }`}
          >
            Privacy policy
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8 pb-24">
        {active === "terms" ? <Terms /> : <Privacy />}
      </div>
    </div>
  );
}

// ============================================================================
// Terms of Service
// ============================================================================
function Terms() {
  return (
    <article className="prose-legal" data-testid="legal-terms">
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-2">
        Terms of service
      </h1>
      <p className="text-sm text-[var(--text-secondary)] mb-8">
        Last updated: {LAST_UPDATED}
      </p>

      <Section title="1. Who we are">
        <p>
          {COMPANY} (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates the website at{" "}
          <strong>{SITE}</strong> and the related Fairway mobile / progressive
          web app (the &quot;App&quot;). By creating an account or using the
          App you agree to these Terms. If you don&apos;t agree, please don&apos;t use
          the App.
        </p>
      </Section>

      <Section title="2. The App in plain English">
        <p>
          Fairway helps you track golf rounds, distances, scores, leagues and
          stats across courses in the UK, Spain and Portugal. Some features are
          free; some require a Fairway Pro subscription.
        </p>
      </Section>

      <Section title="3. Your account">
        <ul>
          <li>You must be 16 or older to create an account.</li>
          <li>You&apos;re responsible for keeping your password safe and for any activity on your account.</li>
          <li>Provide accurate info — registering under someone else&apos;s name or with a fake email isn&apos;t allowed.</li>
          <li>One account per person. You can delete your account any time via Profile → Sign out → contact us.</li>
        </ul>
      </Section>

      <Section title="4. Fairway Pro (subscriptions)">
        <ul>
          <li>Pro is billed via Stripe. Prices and trial length are shown on the Subscribe page before checkout.</li>
          <li>Subscriptions renew automatically until you cancel. Cancel any time from your account — your Pro features stay active until the end of the paid period.</li>
          <li>We offer a 14-day refund window for first-time subscribers if you contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Beyond that, payments are non-refundable except where required by UK consumer law.</li>
          <li>Prices may change with at least 30 days&apos; notice via email. Existing renewals keep the old price for the current billing cycle.</li>
        </ul>
      </Section>

      <Section title="5. User-submitted content (scorecards, course data, league names)">
        <p>
          When you submit course data (e.g. scorecard photos, pin locations,
          tee yardages) you grant {COMPANY} a worldwide, royalty-free,
          perpetual licence to use, display, modify and distribute that
          content as part of the App — including showing it to other users
          and re-distributing the resulting course dataset.
        </p>
        <p>
          You confirm you have the right to submit anything you upload (don&apos;t
          submit other people&apos;s copyrighted scorecards / photos / data unless
          you own them or they&apos;re publicly available).
        </p>
        <p>
          We may moderate, edit or remove any submission at our discretion to
          maintain quality (e.g. fixing obvious typos before publishing a
          scorecard, removing spam, etc.).
        </p>
      </Section>

      <Section title="6. Don't do these things">
        <ul>
          <li>No scraping, reverse-engineering or bulk-downloading the App or course data.</li>
          <li>No automated account creation, spam or abuse of leagues / share codes.</li>
          <li>No uploading anything illegal, defamatory, infringing or harmful.</li>
          <li>No reselling or sub-licensing access to the App.</li>
          <li>No interfering with other users&apos; rounds, leagues or scorecards.</li>
        </ul>
        <p>
          We can suspend or terminate accounts that break these rules without
          notice or refund.
        </p>
      </Section>

      <Section title="7. GPS and on-course safety">
        <p>
          The App uses your phone&apos;s GPS to estimate distance to the pin.
          Accuracy depends on your hardware, sky conditions and the quality of
          our course pin data — it&apos;s a guide, not a tournament-grade
          rangefinder. Always check yardages independently for important
          shots. Don&apos;t use the App while driving or in a way that risks
          your safety or other golfers&apos;.
        </p>
      </Section>

      <Section title="8. Course data accuracy">
        <p>
          Most of our courses are crowdsourced + imported from public sources
          (e.g. OpenStreetMap). We do our best to keep par, yards and pin
          locations correct but we can&apos;t guarantee 100% accuracy. If you
          spot an error, submit a corrected scorecard via the App — we love
          that.
        </p>
      </Section>

      <Section title="9. Intellectual property">
        <p>
          The Fairway name, logo, branding, source code, design and aggregated
          course dataset are owned by {COMPANY}. These Terms grant you a
          personal, non-transferable, revocable licence to use the App; nothing
          else is licensed.
        </p>
      </Section>

      <Section title="10. Disclaimers and liability">
        <p>
          The App is provided &quot;as is&quot;. To the maximum extent permitted by
          law, we exclude all warranties (e.g. fitness for purpose, accuracy of
          GPS distances). We don&apos;t exclude liability for death, personal
          injury caused by our negligence, fraud, or anything else that can&apos;t
          legally be excluded under UK law.
        </p>
        <p>
          Our total liability to you for any claim is limited to the greater of
          (a) the amount you paid us in the 12 months before the claim, or (b)
          £50.
        </p>
      </Section>

      <Section title="11. Changes to these Terms">
        <p>
          We may update these Terms. Material changes will be flagged in the
          App or by email. Continuing to use the App after changes take effect
          means you accept the new Terms.
        </p>
      </Section>

      <Section title="12. Governing law">
        <p>
          These Terms are governed by the laws of England and Wales. Disputes
          go to the courts of England and Wales.
        </p>
      </Section>

      <Section title="13. Contact">
        <p>
          Questions, complaints or a refund request? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </Section>
    </article>
  );
}

// ============================================================================
// Privacy Policy
// ============================================================================
function Privacy() {
  return (
    <article className="prose-legal" data-testid="legal-privacy">
      <h1 className="font-heading font-black tracking-tighter-2 text-4xl text-[var(--primary)] mb-2">
        Privacy policy
      </h1>
      <p className="text-sm text-[var(--text-secondary)] mb-8">
        Last updated: {LAST_UPDATED}
      </p>

      <Section title="1. Who is the data controller?">
        <p>
          {COMPANY} ({SITE}) is the data controller for personal data you give
          us through the App. Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section title="2. What we collect">
        <p><strong>Account info you give us:</strong></p>
        <ul>
          <li>Name, email, hashed password</li>
          <li>Handicap (optional)</li>
          <li>Payment handle (PayPal/Monzo/Revolut username, optional — used so league mates can pay you)</li>
          <li>Profile photo URL (only if you set one)</li>
        </ul>

        <p><strong>App usage data:</strong></p>
        <ul>
          <li>Rounds, scores, putts, club selections, shot distances you log</li>
          <li>Favourite courses, leagues you create or join</li>
          <li>Course data you submit (scorecard photos, pin location corrections)</li>
        </ul>

        <p><strong>Location data:</strong></p>
        <ul>
          <li>
            While you have an active round open, the App reads your live GPS
            position from the device to calculate distance to the pin. We keep
            a short breadcrumb trail of positions per hole in memory; we do not
            store this on our servers.
          </li>
          <li>
            We never track your location when the App is closed or backgrounded.
          </li>
        </ul>

        <p><strong>Payment data:</strong></p>
        <ul>
          <li>
            Subscriptions are processed by Stripe. We never see or store your
            card details — only the Stripe customer / subscription IDs.
          </li>
        </ul>

        <p><strong>Technical data:</strong></p>
        <ul>
          <li>
            IP address, device info, browser version (collected in server logs
            for security and abuse prevention). Retained for 30 days.
          </li>
        </ul>
      </Section>

      <Section title="3. Why we process it (UK GDPR legal bases)">
        <ul>
          <li><strong>Contract</strong>: to give you the App and the Pro features you paid for.</li>
          <li><strong>Legitimate interests</strong>: to keep the App secure, prevent abuse, improve features, and reach out about your account.</li>
          <li><strong>Consent</strong>: for marketing emails (you can opt out at any time).</li>
          <li><strong>Legal obligation</strong>: to retain financial records for tax / accounting (6 years for invoices).</li>
        </ul>
      </Section>

      <Section title="4. Who we share data with">
        <p>We do <strong>not</strong> sell your data. We share it with:</p>
        <ul>
          <li><strong>Stripe</strong> (payment processing) — necessary for billing.</li>
          <li><strong>Resend</strong> (email delivery) — to send password resets and account notifications.</li>
          <li><strong>Google / Gemini Vision</strong> (AI image processing) — only when you submit a scorecard photo. The image is processed transiently and not retained by Google for training.</li>
          <li><strong>MongoDB Atlas</strong> (database hosting) — stores your account + round data inside the EU/UK region.</li>
          <li><strong>Your league mates</strong>: members of a league can see your name, total score and round count within that league. They never see your email, password or payment details.</li>
        </ul>
      </Section>

      <Section title="5. How long we keep your data">
        <ul>
          <li>Account + rounds: as long as your account exists. Delete your account → all rounds, leagues memberships, club bag, scores deleted within 30 days.</li>
          <li>Server logs (IP, device info): 30 days.</li>
          <li>Payment / invoice records: 6 years (UK tax law).</li>
          <li>Inactive accounts: we may purge accounts that haven&apos;t logged in for 2+ years after warning you by email.</li>
        </ul>
      </Section>

      <Section title="6. Your rights">
        <p>Under UK GDPR you have the right to:</p>
        <ul>
          <li><strong>Access</strong> a copy of your data</li>
          <li><strong>Correct</strong> inaccurate data (most of which you can edit yourself in Profile)</li>
          <li><strong>Delete</strong> your account and personal data</li>
          <li><strong>Object</strong> to processing based on legitimate interests</li>
          <li><strong>Port</strong> your data (export rounds, leagues, scores as JSON on request)</li>
          <li><strong>Withdraw consent</strong> for marketing emails any time</li>
          <li><strong>Complain</strong> to the UK ICO (ico.org.uk) if you&apos;re unhappy with how we handle your data</li>
        </ul>
        <p>
          To exercise any of these, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We&apos;ll respond within 30 days.
        </p>
      </Section>

      <Section title="7. Cookies">
        <p>
          We use a small number of strictly-necessary cookies / local-storage
          entries for login, session, and remembering your tee colour /
          favourites. We don&apos;t use third-party tracking or advertising
          cookies. No cookie banner needed under UK PECR for these.
        </p>
      </Section>

      <Section title="8. International transfers">
        <p>
          Our hosting is in the UK / EU. Stripe and Resend may transfer data to
          the US under EU-US Data Privacy Framework / UK extension, which is
          considered adequate under UK GDPR.
        </p>
      </Section>

      <Section title="9. Children">
        <p>
          The App is not intended for users under 16. If we discover an under-16
          account, we&apos;ll delete it.
        </p>
      </Section>

      <Section title="10. Changes to this policy">
        <p>
          We&apos;ll notify you of material changes via email or in-app banner.
          Continuing to use the App after changes means you accept the new
          policy.
        </p>
      </Section>

      <Section title="11. Contact us">
        <p>
          Privacy questions: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </Section>
    </article>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-7">
      <h2 className="font-heading font-bold text-xl text-[var(--primary)] mb-2 mt-6">
        {title}
      </h2>
      <div className="text-[var(--text)] text-[15px] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}
